import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useStrings } from '@/i18n/Localization';
import { MessageRow } from '@/components/MessageRow';
import type { Hex } from '@/store/ids';
import type {
  ConversationRecord,
  MessageRecord,
  OutboxRecord,
  PollRecord,
  ReactionRecord,
} from '@/store/types';
import type { WorkerClient } from '@/transport/workerClient';
import styles from '@/components/Timeline.module.css';

/** Section 14: keyset-load the previous page near 500 px from the top. */
const LOAD_OLDER_THRESHOLD_PX = 500;
/** Section 14: auto-scroll only within 100 px of the bottom, or after own send. */
const NEAR_BOTTOM_PX = 100;
/** Section 14: virtualize with a 20-item overscan. */
const OVERSCAN = 20;
/** Section 14: group adjacent messages from one sender within two minutes. */
const GROUPING_WINDOW_MS = 2 * 60 * 1000;

export interface TimelineProps {
  messages: readonly MessageRecord[];
  outboxByOp: ReadonlyMap<string, OutboxRecord>;
  conversation: ConversationRecord | null;
  ownUserId: string;
  hasMore: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onReply: (message: MessageRecord) => void;
  onEdit: (message: MessageRecord) => void;
  onDelete: (message: MessageRecord) => void;
  onRetry: (opId: string) => void;
  onDiscard: (opId: string) => void;
  /** Changes whenever this account sends, which forces one auto-scroll. */
  ownSendToken: number;
  displayNameFor: (userId: string) => string;

  // --- Phase 2, 3 and 4 ----------------------------------------------------
  worker: WorkerClient | null;
  reactions: readonly ReactionRecord[];
  polls: ReadonlyMap<string, PollRecord>;
  canPin: boolean;
  canDeleteOthers: boolean;
  /** Message identifier the auto-advance queue has selected, or null. */
  autoPlayMessageId: string | null;
  /** Message the reader jumped to from search or the pinned panel. */
  focusMessageId: string | null;
  onForward: (message: MessageRecord) => void;
  onPin: (message: MessageRecord) => void;
  onReact: (message: MessageRecord, reaction: string, active: boolean) => void;
  onVote: (poll: PollRecord, optionIds: Hex[]) => void;
  onClosePoll: (poll: PollRecord) => void;
  onVoicePosition: (message: MessageRecord, positionMs: number) => void;
  onSeen: (message: MessageRecord) => void;
}

/**
 * The virtualized message timeline.
 *
 * Two behaviours here are subtle enough to be worth stating.
 *
 * Scroll anchoring. When an older page is prepended, everything below it moves
 * down by the height of the new content, and a naive render would jump the
 * reader to a different part of the conversation. The correction compares the
 * scroll height recorded at the previous commit with the height after the
 * prepend and adds the difference to `scrollTop`. Under virtualization the
 * container's scroll height is the virtualizer's total size, so the delta is
 * exactly the inserted height, and the message that was first visible stays
 * first visible at the same pixel offset.
 *
 * Auto-scroll. Following new messages is only correct when the reader is at the
 * bottom already or has just sent something. Scrolling someone who is reading
 * history back to the newest message is the single most disruptive thing a chat
 * timeline can do, so anywhere else the arrival is announced with a button
 * instead.
 */
export function Timeline(props: TimelineProps) {
  const { t, formatRelativeDay } = useStrings();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [unseenArrived, setUnseenArrived] = useState(false);

  const virtualizer = useVirtualizer({
    count: props.messages.length,
    getScrollElement: () => scrollRef.current,
    // A first guess only; every row is measured once it renders.
    estimateSize: () => 68,
    overscan: OVERSCAN,
    getItemKey: (index) => props.messages[index]?.id ?? index,
  });

  const previous = useRef({ firstId: '', lastId: '', scrollHeight: 0, count: 0 });

  // Reactions are grouped once per render rather than filtered inside every
  // row: a conversation with a thousand reactions would otherwise walk the
  // whole array once per visible message on every keystroke.
  const reactionsByMessage = useMemo(() => {
    const grouped = new Map<string, ReactionRecord[]>();
    for (const reaction of props.reactions) {
      const existing = grouped.get(reaction.messageId);
      if (existing) {
        existing.push(reaction);
      } else {
        grouped.set(reaction.messageId, [reaction]);
      }
    }
    return grouped;
  }, [props.reactions]);

  // Jumping to a message found in search or the pinned list. The message has to
  // be inside the loaded window; when it is not, the caller widens the window
  // first and this effect runs again once it arrives.
  //
  // `scrolledTo` records which target has already been reached, so that a later
  // page of history — or a new message arriving — does not drag the reader back
  // to a result they have since scrolled away from.
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    const target = props.focusMessageId;
    if (target === null || scrolledTo.current === target) {
      return;
    }
    const index = props.messages.findIndex((message) => message.id === target);
    if (index >= 0) {
      scrolledTo.current = target;
      virtualizer.scrollToIndex(index, { align: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focusMessageId, props.messages.length]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const firstId = props.messages[0]?.id ?? '';
    const lastId = props.messages[props.messages.length - 1]?.id ?? '';
    const before = previous.current;

    const prepended =
      before.firstId !== '' &&
      firstId !== before.firstId &&
      props.messages.some((message) => message.id === before.firstId);

    if (prepended) {
      element.scrollTop += element.scrollHeight - before.scrollHeight;
    } else if (before.lastId !== lastId && props.messages.length > before.count) {
      // Something arrived at the end. Follow it only under the Section 14 rule.
      if (atBottom) {
        virtualizer.scrollToIndex(props.messages.length - 1, { align: 'end' });
      } else {
        setUnseenArrived(true);
      }
    }

    previous.current = {
      firstId,
      lastId,
      scrollHeight: element.scrollHeight,
      count: props.messages.length,
    };
  });

  // An own send always scrolls, regardless of where the reader was: they just
  // acted, and not showing them the result of their own action is worse than
  // moving the viewport.
  useEffect(() => {
    if (props.ownSendToken === 0 || props.messages.length === 0) {
      return;
    }
    virtualizer.scrollToIndex(props.messages.length - 1, { align: 'end' });
    setUnseenArrived(false);
    setAtBottom(true);
    // Deliberately keyed on the send token alone: re-running on every message
    // change would reintroduce unconditional auto-scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ownSendToken]);

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    const nearBottom = distanceFromBottom <= NEAR_BOTTOM_PX;
    setAtBottom(nearBottom);
    if (nearBottom) {
      setUnseenArrived(false);
    }
    if (
      element.scrollTop <= LOAD_OLDER_THRESHOLD_PX &&
      props.hasMore &&
      !props.loadingOlder &&
      props.messages.length > 0
    ) {
      props.onLoadOlder();
    }
  }, [props]);

  const jumpToLatest = () => {
    if (props.messages.length === 0) {
      return;
    }
    virtualizer.scrollToIndex(props.messages.length - 1, { align: 'end' });
    setUnseenArrived(false);
    setAtBottom(true);
  };

  const items = virtualizer.getVirtualItems();

  return (
    <div className={styles.container}>
      <div
        ref={scrollRef}
        className={styles.scroller}
        onScroll={onScroll}
        // `log` announces additions without stealing focus, which is the right
        // role for a transcript that grows while the reader is elsewhere.
        role="log"
        aria-label={t('conversation.timeline')}
        aria-live="polite"
        tabIndex={0}
      >
        {props.loadingOlder ? (
          <p className={styles.loading} role="status">
            {t('conversation.loadingOlder')}
          </p>
        ) : null}
        {!props.hasMore && props.messages.length > 0 ? (
          <p className={styles.historyStart}>{t('conversation.historyStart')}</p>
        ) : null}

        {props.messages.length === 0 ? (
          <p className={styles.empty}>{t('conversation.empty')}</p>
        ) : (
          <ol className={styles.list} style={{ height: `${String(virtualizer.getTotalSize())}px` }}>
            {items.map((item) => {
              const message = props.messages[item.index];
              if (!message) {
                return null;
              }
              const previousMessage = item.index > 0 ? props.messages[item.index - 1] : undefined;
              const grouped =
                previousMessage !== undefined &&
                previousMessage.senderId === message.senderId &&
                message.createdAt - previousMessage.createdAt <= GROUPING_WINDOW_MS &&
                !previousMessage.deleted &&
                !message.deleted;

              /*
               * A day chip on the first message of each local day, and on the
               * very first message in the window.
               *
               * Compared in local time via the date string rather than by
               * dividing the timestamp into 86,400-second buckets: the reader's
               * day boundary is their midnight, which moves with their time zone
               * and with daylight saving, and arithmetic on UTC milliseconds puts
               * the separator in the wrong place for most of the world.
               */
              const dayLabel =
                previousMessage === undefined ||
                new Date(previousMessage.createdAt).toDateString() !==
                  new Date(message.createdAt).toDateString()
                  ? formatRelativeDay(message.createdAt)
                  : null;

              return (
                <li
                  key={item.key}
                  // Measured rather than estimated, so variable-height messages
                  // and 200 % text scaling both produce correct offsets.
                  ref={virtualizer.measureElement}
                  data-index={item.index}
                  className={styles.item}
                  style={{ transform: `translateY(${String(item.start)}px)` }}
                >
                  <MessageRow
                    message={message}
                    outbox={message.opId ? props.outboxByOp.get(message.opId) : undefined}
                    conversation={props.conversation}
                    worker={props.worker}
                    ownUserId={props.ownUserId}
                    grouped={grouped}
                    dayLabel={dayLabel}
                    senderName={
                      // A channel post is displayed as the channel, and the
                      // server sends that name with the message. It is never
                      // "you", even for the administrator who published it.
                      message.sender?.kind === 'CHANNEL'
                        ? message.sender.displayName
                        : message.senderId === null
                          ? ''
                          : message.senderId === props.ownUserId
                            ? t('conversation.you')
                            : props.displayNameFor(message.senderId)
                    }
                    reactions={reactionsByMessage.get(message.id) ?? []}
                    poll={
                      message.content.embeddedId === null
                        ? null
                        : props.polls.get(message.content.embeddedId) ?? null
                    }
                    canPin={props.canPin}
                    canDeleteOthers={props.canDeleteOthers}
                    autoPlayVoice={props.autoPlayMessageId === message.id}
                    onReply={props.onReply}
                    onEdit={props.onEdit}
                    onDelete={props.onDelete}
                    onForward={props.onForward}
                    onPin={props.onPin}
                    onReact={props.onReact}
                    onVote={props.onVote}
                    onClosePoll={props.onClosePoll}
                    onVoicePosition={props.onVoicePosition}
                    onSeen={props.onSeen}
                    onRetry={props.onRetry}
                    onDiscard={props.onDiscard}
                  />
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {!atBottom || unseenArrived ? (
        <button type="button" className={styles.jump} onClick={jumpToLatest}>
          {unseenArrived ? t('conversation.newMessages') : t('conversation.jumpToLatest')}
        </button>
      ) : null}
    </div>
  );
}
